'use client';

import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface GeneratedQuest {
  title: string;
  description: string;
  quest_type: string;
  module_target: string;
  resonance_hook: string;
  difficulty: 'entry' | 'intermediate' | 'deep';
  amanah_prompt: string;
  href: string;
  color: string;
  icon: string;
}

export interface QuestContext {
  role: string;
  topContentTypes: string[];
  topNames: string[];
  topKeys: string[];
  totalInteractions: number;
  recentModules: string[];
  bookmarkCount: number;
  verseClickCount: number;
  moduleVisitMap: Record<string, number>;
}

// ─── ROLE QUEST TEMPLATES ─────────────────────────────────────────────────────

const ROLE_QUEST_POOLS: Record<string, GeneratedQuest[]> = {
  'inner-seeker': [
    {
      title: 'Complete the Leaf Sequence',
      description: 'Walk through the Mycorrhizal sequence in Keys to Kulli Haal — from root observation to recognition of Al-Muhyi (the Life-Giver). Note one thing you observe in your own locality.',
      quest_type: 'observation',
      module_target: 'keys',
      resonance_hook: 'Al-Muhyi · Mycorrhizal · Leaf Sequence',
      difficulty: 'entry',
      amanah_prompt: 'What living system near you reflects Al-Muhyi today?',
      href: '/keys-to-kulli-haal',
      color: '#d4a82a',
      icon: '⌘',
    },
    {
      title: 'Walk the IRL Lens — All 8 Layers',
      description: 'Begin at Mata (encounter) and move through all eight layers of the Inner Reality Lens. Do not rush. Each layer is a recognition, not a checkbox.',
      quest_type: 'walkthrough',
      module_target: 'irl',
      resonance_hook: 'Mata → Uboor · Recognition Arc',
      difficulty: 'intermediate',
      amanah_prompt: 'At which layer did you pause longest? What was being recognized?',
      href: '/inner-reality-lens',
      color: '#2d8a87',
      icon: '◎',
    },
    {
      title: 'Bookmark 5 Names That Resonate',
      description: 'Open the Names Reference and find five Names of Allah that resonate with your current state. Save them to your collection. Notice the pattern — what does it reveal about your Haal?',
      quest_type: 'collection',
      module_target: 'names',
      resonance_hook: 'Names Reference · Haal · Recognition',
      difficulty: 'entry',
      amanah_prompt: 'What do the five Names you chose have in common?',
      href: '/names-reference',
      color: '#8a8ab8',
      icon: '✦',
    },
    {
      title: 'Find Your Verse — Ayat in Creation',
      description: 'Explore Ayat in Creation and find one verse that describes something you have personally observed in the natural world. Bookmark it. Return to it tomorrow.',
      quest_type: 'verse',
      module_target: 'ayat',
      resonance_hook: 'Ayat · Al-Zahir · Observation',
      difficulty: 'entry',
      amanah_prompt: 'Where in your locality does this Ayat become visible?',
      href: '/ayat-in-creation',
      color: '#2d7a47',
      icon: '☽',
    },
    {
      title: 'Read the Open Letter — Sections 1–8',
      description: 'Begin the Open Letter: Deep Green from the beginning. Read through Section 8 (The Five Haals). Take notes on what you recognize from your own experience.',
      quest_type: 'reading',
      module_target: 'letter',
      resonance_hook: 'Open Letter · Five Haals · Foundation',
      difficulty: 'intermediate',
      amanah_prompt: 'Which of the Five Haals describes your current state most accurately?',
      href: '/open-letter',
      color: '#c9a84c',
      icon: '◈',
    },
    {
      title: 'Complete the SL Glossary Deep Read',
      description: 'Search the SL Glossary for three terms you do not yet know. Read their full definitions, etymologies, and usage examples. Cross-link them to Names you have already bookmarked.',
      quest_type: 'scholarship',
      module_target: 'glossary',
      resonance_hook: 'SL Glossary · Sacred Language · Etymology',
      difficulty: 'deep',
      amanah_prompt: 'How does the root of each term connect to the Names you know?',
      href: '/sl-glossary',
      color: '#c9a84c',
      icon: '◈',
    },
  ],

  'community-caretaker': [
    {
      title: 'Map Your City\'s Gaps in the MRG',
      description: 'Open the Muslim Resource Guide for your city. Identify three resource categories that are missing or underrepresented. This is your Amanah map — what your community is entrusted to fill.',
      quest_type: 'mapping',
      module_target: 'mrg',
      resonance_hook: 'MRG · Amanah · Community Gaps',
      difficulty: 'entry',
      amanah_prompt: 'Which gap is yours to address? What is the first step?',
      href: '/muslim-resource-guide',
      color: '#2d7a47',
      icon: '⬡',
    },
    {
      title: 'Explore Justice-Involved Resources',
      description: 'Read through the justice-involved section of the MRG. Find one resource relevant to someone you know. Share it with them — or bookmark it for when it is needed.',
      quest_type: 'justice',
      module_target: 'mrg',
      resonance_hook: 'Justice · Mizan · Al-Adl',
      difficulty: 'entry',
      amanah_prompt: 'Who in your community needs this resource right now?',
      href: '/muslim-resource-guide',
      color: '#2d8a87',
      icon: '⌘',
    },
    {
      title: 'Connect the IRL Lens to Your Community',
      description: 'Walk through the IRL Lens with your community in mind — not just yourself. At each layer, ask: what does this look like at the community level? What is the collective Haal?',
      quest_type: 'community-observation',
      module_target: 'irl',
      resonance_hook: 'IRL Lens · Community · Collective Haal',
      difficulty: 'intermediate',
      amanah_prompt: 'What is the collective Haal of your community right now?',
      href: '/inner-reality-lens',
      color: '#2d8a87',
      icon: '◎',
    },
    {
      title: 'Study Al-Adl and Al-Haqq in the Names Reference',
      description: 'Open the Names Reference and study Al-Adl (The Just) and Al-Haqq (The Truth). Read their full entries, cross-links, and IRL tiles. How do these Names ground your justice work?',
      quest_type: 'scholarship',
      module_target: 'names',
      resonance_hook: 'Al-Adl · Al-Haqq · Justice Framework',
      difficulty: 'intermediate',
      amanah_prompt: 'How does Al-Adl change how you approach a current community challenge?',
      href: '/names-reference',
      color: '#8a8ab8',
      icon: '✦',
    },
    {
      title: 'Read the Bridge IRL Application',
      description: 'Explore the Bridge IRL Application — the connection between inner recognition and outer community action. Find one bridge that applies to your current work.',
      quest_type: 'bridge',
      module_target: 'bridge-irl',
      resonance_hook: 'Bridge IRL · Amanah · Action',
      difficulty: 'intermediate',
      amanah_prompt: 'What is the bridge between your inner recognition and your community action?',
      href: '/bridge-irl-application',
      color: '#c9a84c',
      icon: '◈',
    },
  ],

  'academic': [
    {
      title: 'Map the Names Ontology — 10 Names',
      description: 'Select 10 Names of Allah from the Names Reference. Map their relationships — which Names share roots? Which are paired? Which form a sequence? Build your own ontological map.',
      quest_type: 'scholarship',
      module_target: 'names',
      resonance_hook: 'Names · Ontology · Sacred Language',
      difficulty: 'deep',
      amanah_prompt: 'What does the structure of the Names reveal about the nature of reality?',
      href: '/names-reference',
      color: '#d4a82a',
      icon: '✦',
    },
    {
      title: 'Cross-Reference Ayat with the SL Glossary',
      description: 'Find three verses in Ayat in Creation. For each verse, find the corresponding Sacred Language terms in the SL Glossary. Build a cross-reference map.',
      quest_type: 'cross-reference',
      module_target: 'ayat',
      resonance_hook: 'Ayat · SL Glossary · Cross-Reference',
      difficulty: 'deep',
      amanah_prompt: 'What pattern emerges when Quranic vocabulary meets Sacred Language?',
      href: '/ayat-in-creation',
      color: '#2d7a47',
      icon: '☽',
    },
    {
      title: 'Study the Five Keys as Methodology',
      description: 'Read Keys to Kulli Haal as a methodological framework — not just curriculum. How do the Five Keys function as an epistemological system? What are their limits?',
      quest_type: 'methodology',
      module_target: 'keys',
      resonance_hook: 'Five Keys · Epistemology · Methodology',
      difficulty: 'deep',
      amanah_prompt: 'What does this methodology assume about the nature of knowledge?',
      href: '/keys-to-kulli-haal',
      color: '#d4a82a',
      icon: '⌘',
    },
    {
      title: 'Read the Open Letter — Full Annotation',
      description: 'Read the Open Letter: Deep Green in full. Annotate each section with your own scholarly notes. Identify three claims that require further investigation.',
      quest_type: 'annotation',
      module_target: 'letter',
      resonance_hook: 'Open Letter · Scholarship · Foundation',
      difficulty: 'deep',
      amanah_prompt: 'What is the strongest claim in the Open Letter? What evidence supports it?',
      href: '/open-letter',
      color: '#c9a84c',
      icon: '◈',
    },
    {
      title: 'Explore the SL Glossary — Etymology Deep Dive',
      description: 'Select five terms from the SL Glossary with Arabic roots. Trace each root through classical Arabic lexicons. How does the Haal Project\'s usage compare to classical usage?',
      quest_type: 'etymology',
      module_target: 'glossary',
      resonance_hook: 'SL Glossary · Etymology · Classical Arabic',
      difficulty: 'deep',
      amanah_prompt: 'Where does the framework\'s vocabulary diverge from classical usage, and why?',
      href: '/sl-glossary',
      color: '#c9a84c',
      icon: '◈',
    },
  ],

  'activist': [
    {
      title: 'Map Environmental Justice Through the IRL Lens',
      description: 'Walk the IRL Lens with environmental justice in mind. At each layer, identify one environmental injustice in your locality. Connect it to a Name of Allah — Al-Adl, Al-Haqq, or Al-Fasad.',
      quest_type: 'justice-mapping',
      module_target: 'irl',
      resonance_hook: 'IRL Lens · Environmental Justice · Al-Adl',
      difficulty: 'intermediate',
      amanah_prompt: 'What is the environmental Haal of your locality right now?',
      href: '/inner-reality-lens',
      color: '#2d8a87',
      icon: '◎',
    },
    {
      title: 'Find Justice Resources for Your Community',
      description: 'Open the MRG and find three resources specifically relevant to justice work in your city. Bookmark them. Share at least one with someone who needs it this week.',
      quest_type: 'resource-mapping',
      module_target: 'mrg',
      resonance_hook: 'MRG · Justice · Community Action',
      difficulty: 'entry',
      amanah_prompt: 'What is the most urgent justice need in your community right now?',
      href: '/muslim-resource-guide',
      color: '#2d7a47',
      icon: '⬡',
    },
    {
      title: 'Study Al-Fasad and Al-Islah in the Names Reference',
      description: 'Research Al-Fasad (corruption/disruption) and Al-Islah (reform/restoration) as concepts in the framework. How do they frame your organizing work?',
      quest_type: 'framework',
      module_target: 'names',
      resonance_hook: 'Al-Fasad · Al-Islah · Justice Framework',
      difficulty: 'intermediate',
      amanah_prompt: 'Where is Al-Fasad most visible in your locality? What would Al-Islah look like?',
      href: '/names-reference',
      color: '#8a8ab8',
      icon: '✦',
    },
    {
      title: 'Read the Bridge Environment Design',
      description: 'Explore the Bridge Environment Design section. How does the framework connect ecological observation to environmental justice? Find one bridge that applies to your current campaign.',
      quest_type: 'bridge',
      module_target: 'bridge-env',
      resonance_hook: 'Bridge Environment · Amanah · Justice',
      difficulty: 'intermediate',
      amanah_prompt: 'What environmental design principle could strengthen your organizing work?',
      href: '/bridge-environment-design',
      color: '#c9a84c',
      icon: '◈',
    },
    {
      title: 'Connect Mizan to Your Justice Work',
      description: 'Study the concept of Mizan (balance/scale) in the SL Glossary and Names Reference. How does Mizan as a framework differ from Western justice frameworks? Apply it to one current issue.',
      quest_type: 'framework',
      module_target: 'glossary',
      resonance_hook: 'Mizan · Justice · Sacred Language',
      difficulty: 'deep',
      amanah_prompt: 'What does Mizan demand of you in your current justice work?',
      href: '/sl-glossary',
      color: '#c9a84c',
      icon: '◈',
    },
  ],

  'explorer': [
    {
      title: 'Begin the Guided Walkthrough',
      description: 'Start with the Guided Walkthrough — a structured introduction to the Haal Project framework. Complete all seven checkpoints.',
      quest_type: 'onboarding',
      module_target: 'walkthrough',
      resonance_hook: 'Guided Walkthrough · Introduction · Framework',
      difficulty: 'entry',
      amanah_prompt: 'What brought you here? What are you looking for?',
      href: '/guided-walkthrough',
      color: '#c9a84c',
      icon: '◈',
    },
    {
      title: 'Explore the IRL Lens',
      description: 'Open the Inner Reality Lens and walk through the first three layers. You do not need to complete it — just begin. Notice what you notice.',
      quest_type: 'exploration',
      module_target: 'irl',
      resonance_hook: 'IRL Lens · Mata · First Encounter',
      difficulty: 'entry',
      amanah_prompt: 'What did you encounter in the first layer?',
      href: '/inner-reality-lens',
      color: '#2d8a87',
      icon: '◎',
    },
    {
      title: 'Find Your Caretaker Role',
      description: 'Open the Caretaker Selector on the homepage. Explore the six profiles. Which one resonates most with your current state? Select it — your journey will personalize from there.',
      quest_type: 'role-selection',
      module_target: 'homepage',
      resonance_hook: 'Caretaker Role · Haal · Recognition',
      difficulty: 'entry',
      amanah_prompt: 'Which role describes your current Amanah most accurately?',
      href: '/',
      color: '#c9a84c',
      icon: '⌘',
    },
  ],

  'local-resource': [
    {
      title: 'List Your Resource in the MRG',
      description: 'Add your service, masjid, clinic, or organization to the Muslim Resource Guide. Make yourself findable to those who need you.',
      quest_type: 'listing',
      module_target: 'mrg',
      resonance_hook: 'MRG · Amanah · Community Service',
      difficulty: 'entry',
      amanah_prompt: 'Who is your resource for? Who cannot find you right now?',
      href: '/muslim-resource-guide',
      color: '#5a5a8a',
      icon: '⬡',
    },
    {
      title: 'Connect Your Service to the Framework',
      description: 'Read the Open Letter sections on Provision and Caretaking. How does your service fit within the framework of Amanah? Write one sentence that describes your service in Sacred Language terms.',
      quest_type: 'framework',
      module_target: 'letter',
      resonance_hook: 'Open Letter · Provision · Amanah',
      difficulty: 'intermediate',
      amanah_prompt: 'What Name of Allah does your service most directly embody?',
      href: '/open-letter',
      color: '#c9a84c',
      icon: '◈',
    },
    {
      title: 'Explore the Community Network',
      description: 'Visit the Community page and explore what other caretakers are building. Find one organization or resource that complements yours. Reach out.',
      quest_type: 'networking',
      module_target: 'community',
      resonance_hook: 'Community · Network · Caretakers',
      difficulty: 'entry',
      amanah_prompt: 'Who is your natural partner in this work?',
      href: '/community',
      color: '#2d7a47',
      icon: '⬡',
    },
  ],
};

// ─── RESONANCE-BASED QUEST SELECTION ─────────────────────────────────────────

function selectQuestsByResonance(
  pool: GeneratedQuest[],
  context: QuestContext,
  count: number
): GeneratedQuest[] {
  // Score each quest based on resonance context
  const scored = pool.map(quest => {
    let score = 0;

    // Boost quests targeting modules the user has NOT visited (encourage exploration)
    const visited = context.moduleVisitMap[quest.module_target] || 0;
    if (visited === 0) score += 3; // Unvisited module — high priority
    else if (visited < 3) score += 1; // Low visit count — moderate priority

    // Boost quests that connect to user's top content types
    if (context.topContentTypes.some(t => quest.resonance_hook.toLowerCase().includes(t.toLowerCase()))) {
      score += 2;
    }

    // Boost quests that connect to user's top Names
    if (context.topNames.some(n => quest.resonance_hook.toLowerCase().includes(n.toLowerCase()))) {
      score += 2;
    }

    // Difficulty progression: if low interactions, prefer entry; if high, prefer deep
    if (context.totalInteractions < 5 && quest.difficulty === 'entry') score += 2;
    if (context.totalInteractions >= 10 && quest.difficulty === 'intermediate') score += 1;
    if (context.totalInteractions >= 25 && quest.difficulty === 'deep') score += 2;

    // Boost if quest targets a recently visited module (reinforce momentum)
    if (context.recentModules.includes(quest.module_target)) score += 1;

    // Add slight randomness to prevent identical quest sets
    score += Math.random() * 0.5;

    return { quest, score };
  });

  // Sort by score descending, take top `count`
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map(s => s.quest);
}

// ─── MAIN QUEST GENERATOR ─────────────────────────────────────────────────────

export async function generateQuestsForRole(
  role: string,
  context: QuestContext,
  count: 3 | 4 | 5 = 4
): Promise<GeneratedQuest[]> {
  const pool = ROLE_QUEST_POOLS[role] || ROLE_QUEST_POOLS['explorer'];
  return selectQuestsByResonance(pool, context, Math.min(count, pool.length));
}

// ─── SAVE QUESTS TO SUPABASE ──────────────────────────────────────────────────

export async function saveGeneratedQuests(
  quests: GeneratedQuest[],
  role: string,
  userId?: string
): Promise<void> {
  const supabase = createClient();

  // Deactivate old quests for this user/role
  if (userId) {
    await supabase
      .from('caretaker_quests')
      .update({ quest_status: 'saved' })
      .eq('user_role', role)
      .neq('quest_status', 'completed');
  }

  // Insert new quests
  const rows = quests.map(q => ({
    user_role: role,
    ward: 'personal',
    city: 'platform',
    tile_id: q.module_target,
    quest_title: q.title,
    quest_desc: q.description,
    ecological_score: q.difficulty === 'entry' ? 1 : q.difficulty === 'intermediate' ? 2 : 3,
    social_score: q.difficulty === 'entry' ? 1 : q.difficulty === 'intermediate' ? 2 : 3,
    quest_status: 'active',
  }));

  await supabase.from('caretaker_quests').insert(rows);
}

// ─── BUILD QUEST CONTEXT FROM RESONANCE DATA ─────────────────────────────────

export function buildQuestContext(
  role: string,
  resonanceSummary: { topContentTypes: string[]; topNames: string[]; topKeys: string[]; totalInteractions: number },
  history: { content_type: string; resonance_type: string }[],
  bookmarkCount: number,
  moduleVisitMap: Record<string, number>
): QuestContext {
  const recentModules = history
    .filter(h => h.resonance_type === 'module_visit')
    .slice(0, 5)
    .map(h => h.content_type);

  const verseClickCount = history.filter(h => h.resonance_type === 'verse_click').length;

  return {
    role,
    topContentTypes: resonanceSummary.topContentTypes,
    topNames: resonanceSummary.topNames,
    topKeys: resonanceSummary.topKeys,
    totalInteractions: resonanceSummary.totalInteractions,
    recentModules,
    bookmarkCount,
    verseClickCount,
    moduleVisitMap,
  };
}
